// Mouse, touch and keyboard. Everything a player does to the game goes out as a command through NET().send,
// so solo and online play take exactly the same path.
import { G, S, regionAt, stationAt, lineAt, segAt, upgradeTarget, validSpot, snapToLine } from '../sim/game.js';
import { UI, me, selLine } from './ui-state.js';
import { cvs, toWorld, zoomAt, panBy, resetCam } from './render.js';
import { capAt, trainAt } from './lineui.js';
import { hud, toast, $ } from './hud.js';
import { Music } from './audio.js';
import { NET, getScreen, actions } from './main.js';

const send = cmd => NET() && NET().send(cmd);
const playing = () => getScreen() === 'play' && G && G.phase === 'play' && me() && me().alive && !UI.demo;
const inputScreen = () => (getScreen() === 'play' || getScreen() === 'draft') && !UI.demo;

// ---------- canvas: build, drag lines, select, pan, pinch ----------
const touches = new Map();
let pinch = null;

function startPan(e, w) { UI.pan = { sx: e.clientX, sy: e.clientY, w, moved: false, button: e.button }; }

cvs.addEventListener('pointerdown', e => {
  if (!G || !inputScreen()) return;
  cvs.setPointerCapture(e.pointerId);
  touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (touches.size === 2) {
    // a second finger turns any gesture into pinch-zoom and pan
    const [a, b] = [...touches.values()];
    pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
    UI.drag = null; UI.pan = null; UI.bend = null; UI.unhook = null; UI.carry = null;
    return;
  }
  const w = toWorld(e);
  if (e.button === 1 || G.phase === 'draft') { startPan(e, w); return; }
  if (!playing()) { startPan(e, w); return; }
  const p = me();
  if (e.button === 2) {
    if (UI.placing) { UI.placing = null; return; }
    const L = lineAt(p, w);
    if (L) { send({ type: 'removeLine', line: L.id }); if (UI.selLine === L.id) UI.selLine = null; }
    return;
  }
  if (UI.placing && (UI.placing.kind === 'warehouse' || UI.placing.kind === 'customs')) {
    const kind = UI.placing.kind, st = upgradeTarget(p, w, kind);
    if (!st) { toast(kind === 'customs' ? 'Click one of your gates (one without customs yet).' : 'Click one of your stations (one without a warehouse yet).'); return; }
    send({ type: 'upgradeStation', kind, station: st.id }); UI.placing = null;
    return;
  }
  if (UI.placing) {
    const snap = snapToLine(p, w), spot = snap ? snap.pos : w;
    if (!validSpot(p, spot)) { toast(snap ? 'Not enough room on that stretch of line.' : 'Too close to the border or another station.'); return; }
    send({ type: 'build', kind: UI.placing.kind, shape: UI.placing.shape, x: w.x, y: w.y });
    UI.placing = null;
    return;
  }
  // a line's buffer stop: grab it to extend that line, or drag it back over its last stop to shorten it
  const cap = capAt(p, w);
  if (cap) { UI.selLine = cap.L.id; UI.drag = { from: cap.station, cur: w }; return; }
  const s = stationAt(w, UI.seat), sel = selLine();
  if (s) {
    // altering the selected line: a stop in its middle can be dragged off it (or onto another station)
    if (sel && sel.st.includes(s.id) && (sel.loop || (sel.st[0] !== s.id && sel.st[sel.st.length - 1] !== s.id))) {
      UI.unhook = { line: sel.id, s, sx: e.clientX, sy: e.clientY, cur: null, to: null }; return;
    }
    // a station that is not on the selected line: this is not altering it, so start fresh
    if (sel && !sel.st.includes(s.id)) UI.selLine = null;
    UI.drag = { from: s, cur: w }; return;
  }
  // a running train: carry it to another of your lines, or off into the sidings
  const tr = trainAt(p, w);
  if (tr) { UI.carry = { line: tr.L.id, i: tr.i, sx: e.clientX, sy: e.clientY, cur: null, over: null }; return; }
  // grabbing a stretch of line lets you pull it onto a station, so the line calls there on the way
  const hit = segAt(p, w);
  if (hit) {
    const n = hit.L.st.length;
    UI.bend = { line: hit.L.id, i: hit.i, a: S(hit.L.st[hit.i]), b: S(hit.L.st[(hit.i + 1) % n]), sx: e.clientX, sy: e.clientY, w, cur: null, to: null };
    return;
  }
  startPan(e, w);
});
// the station a bent line would call at: one of yours, not already on that line
function bendTarget(w) {
  const L = me().lines.find(L => L.id === UI.bend.line), s = stationAt(w, UI.seat);
  return L && s && !L.st.includes(s.id) ? s : null;
}

cvs.addEventListener('pointermove', e => {
  if (!G) return;
  if (touches.has(e.pointerId)) touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (pinch && touches.size >= 2) {
    const [a, b] = [...touches.values()], d = Math.hypot(a.x - b.x, a.y - b.y), cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
    panBy(cx - pinch.cx, cy - pinch.cy);
    if (pinch.d > 10) zoomAt(cx, cy, d / pinch.d);
    pinch = { d, cx, cy };
    return;
  }
  if (UI.pan) {
    const dx = e.clientX - UI.pan.sx, dy = e.clientY - UI.pan.sy;
    if (!UI.pan.moved && Math.hypot(dx, dy) > 5) UI.pan.moved = true;
    if (UI.pan.moved) { panBy(dx, dy); UI.pan.sx = e.clientX; UI.pan.sy = e.clientY; cvs.style.cursor = 'grabbing'; return; }
  }
  const w = toWorld(e); UI.mouse = w;
  if (G.phase === 'draft') { const r = regionAt(w); UI.hover = r ? r.id : null; cvs.style.cursor = r ? 'pointer' : 'default'; return; }
  const moved = o => Math.hypot(e.clientX - o.sx, e.clientY - o.sy) > 5;
  if (UI.unhook && playing()) {
    if (UI.unhook.cur || moved(UI.unhook)) {
      UI.unhook.cur = w;
      const L = me().lines.find(L => L.id === UI.unhook.line), t = stationAt(w, UI.seat);
      UI.unhook.to = L && t && !L.st.includes(t.id) ? t : null;
      cvs.style.cursor = 'grabbing'; return;
    }
  }
  if (UI.carry && playing()) {
    if (UI.carry.cur || moved(UI.carry)) {
      UI.carry.cur = w;
      const hit = segAt(me(), w);
      UI.carry.over = hit && hit.L.id !== UI.carry.line ? hit.L : null;
      cvs.style.cursor = 'grabbing'; return;
    }
  }
  if (UI.bend && playing()) {
    if (!UI.bend.cur && Math.hypot(e.clientX - UI.bend.sx, e.clientY - UI.bend.sy) > 5) UI.bend.cur = w;
    if (UI.bend.cur) { UI.bend.cur = w; UI.bend.to = bendTarget(w); cvs.style.cursor = 'grabbing'; return; }
  }
  if (UI.drag && playing()) {
    UI.drag.cur = w;
    const s = stationAt(w, UI.seat);
    if (s && s !== UI.drag.from) { send({ type: 'link', a: UI.drag.from.id, b: s.id, line: UI.selLine }); UI.drag.from = s; }
  }
  // pointing at one of your own lines lights it up (never a rival's, which would only confuse)
  const over = playing() && !UI.drag && !UI.pan ? segAt(me(), w) : null;
  UI.hoverMap = over ? over.L.id : null;
  cvs.style.cursor = UI.placing ? 'crosshair' : playing() && (stationAt(w, UI.seat) || capAt(me(), w) || trainAt(me(), w)) ? 'grab' : 'default';
});

function endPointer(e) {
  touches.delete(e.pointerId);
  if (touches.size < 2) pinch = null;
  if (!G) return;
  if (UI.pan && !UI.pan.moved && UI.pan.button !== 1) clickAt(UI.pan.w);
  if (UI.drag) send({ type: 'endDrag' });
  if (UI.unhook && UI.unhook.cur && playing()) send({ type: 'moveStop', line: UI.unhook.line, station: UI.unhook.s.id, to: UI.unhook.to ? UI.unhook.to.id : null });
  if (UI.carry && UI.carry.cur && playing()) {
    const { over, cur } = UI.carry;
    // onto a line: it joins at that line's stop nearest the drop; on open ground: back to the sidings
    const at = over ? over.st.reduce((b, id, k) => { const q = S(id); const d = q ? Math.hypot(q.x - cur.x, q.y - cur.y) : Infinity; return d < b.d ? { d, k } : b; }, { d: Infinity, k: 0 }).k : null;
    send({ type: 'moveTrain', line: UI.carry.line, train: UI.carry.i, to: over ? over.id : null, at });
  }
  if (UI.bend) {
    if (!UI.bend.cur) clickAt(UI.bend.w);
    else if (UI.bend.to && playing()) send({ type: 'insertStop', line: UI.bend.line, seg: UI.bend.i, station: UI.bend.to.id });
  }
  UI.pan = null; UI.drag = null; UI.bend = null; UI.unhook = null; UI.carry = null;
}
cvs.addEventListener('pointerup', endPointer);
cvs.addEventListener('pointercancel', endPointer);

// a press that never moved is a click: claim a region in the draft, select a line in play
function clickAt(w) {
  if (G.phase === 'draft') { const r = regionAt(w); if (r && inputScreen()) { send({ type: 'claim', rid: r.id }); actions('refreshDraft'); } return; }
  if (playing()) { const L = lineAt(me(), w); UI.selLine = L ? L.id : null; }
}

cvs.addEventListener('wheel', e => {
  if (!G || !inputScreen()) return;
  e.preventDefault();
  // pinch (ctrl+wheel) and notched mouse wheels zoom; two-finger trackpad scrolls pan
  const mouseWheel = e.deltaMode === 1 || (e.deltaX === 0 && Math.abs(e.deltaY) >= 40);
  if (e.ctrlKey || mouseWheel) zoomAt(e.clientX, e.clientY, Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015) * (e.deltaMode === 1 ? 30 : 1)));
  else panBy(-e.deltaX, -e.deltaY);
}, { passive: false });
cvs.addEventListener('contextmenu', e => e.preventDefault());

// hovering a line's chip in the bar lights that line up on the map
const linesBar = document.getElementById('lines');
linesBar.addEventListener('pointerover', e => { const b = e.target.closest('.sw[data-v]'); UI.hoverChip = b && b.dataset.v !== 'new' ? +b.dataset.v : null; });
linesBar.addEventListener('pointerleave', () => { UI.hoverChip = null; });

// ---------- buttons: one delegated handler for every [data-a] ----------
function press(e) {
  const b = e.target.closest && e.target.closest('[data-a]');
  if (!b || b.disabled) return;
  e.stopPropagation();
  const a = b.dataset.a, v = b.dataset.v;
  const handled = actions(a, v, b);
  if (!handled && playing()) gameAction(a, v);
  hud();
}
// a mouse presses on the way down, which feels instant; a finger presses on a finished tap (click),
// so starting a scroll on a button in a menu never sets it off
let lastPointer = 'mouse';
document.addEventListener('pointerdown', e => { lastPointer = e.pointerType; if (e.pointerType === 'mouse') press(e); }, true);
document.addEventListener('click', e => { if (lastPointer !== 'mouse') press(e); }, true);

function gameAction(a, v) {
  const p = me();
  switch (a) {
    case 'target': send({ type: 'target', v: v === 'auto' ? 'auto' : +v }); break;
    case 'rateDown': send({ type: 'rate', delta: -1 }); break;
    case 'rateUp': send({ type: 'rate', delta: 1 }); break;
    case 'upgrade': send({ type: 'upgradeRate' }); break;
    case 'depot': case 'recycler': UI.placing = UI.placing && UI.placing.kind === a && UI.placing.shape === v ? null : { kind: a, shape: v }; UI.panelOpen = false; break;
    case 'warehouse': case 'customs': UI.placing = UI.placing && UI.placing.kind === a ? null : { kind: a }; UI.panelOpen = false; break;   // phones: get the sheet out of the way to place
    case 'bank': send({ type: 'bank' }); break;
    case 'surge': send({ type: 'surge' }); break;
    case 'annex': send({ type: 'annex', rid: +v }); break;
    case 'train': send({ type: 'addTrain', line: UI.selLine }); break;
    case 'carriage': send({ type: 'addCarriage', line: UI.selLine }); break;
    case 'line': send({ type: 'buyLine' }); break;
    case 'sel': UI.selLine = v === 'new' ? (UI.selLine === 'new' ? null : 'new') : p.lines.some(L => L.id === +v) ? +v : null; break;
    case 'del': send({ type: 'removeLine', line: +v }); if (UI.selLine === +v) UI.selLine = null; break;
    case 'cheatCoins': send({ type: 'cheat', what: 'coins', n: +v }); break;
    case 'cheatKill': send({ type: 'cheat', what: 'kill' }); break;
    case 'cheatRush': send({ type: 'cheat', what: 'rush' }); break;
  }
}

// ---------- keyboard ----------
document.addEventListener('keydown', e => {
  if (e.target.closest && e.target.closest('input')) return;   // typing a name or room code
  const sc = getScreen();
  if ((sc === 'play' || sc === 'draft') && !e.metaKey && !e.ctrlKey && !UI.demo) {
    const pan = { ArrowLeft: [1, 0], a: [1, 0], A: [1, 0], ArrowRight: [-1, 0], d: [-1, 0], D: [-1, 0], ArrowUp: [0, 1], w: [0, 1], W: [0, 1], ArrowDown: [0, -1], s: [0, -1], S: [0, -1] }[e.key];
    if (pan) { panBy(pan[0] * 70, pan[1] * 70); e.preventDefault(); return; }
    if (e.key === '+' || e.key === '=') { zoomAt(innerWidth / 2, innerHeight / 2, 1.25); return; }
    if (e.key === '-' || e.key === '_') { zoomAt(innerWidth / 2, innerHeight / 2, 0.8); return; }
    if (e.key === '0') { resetCam(); return; }
  }
  if ((e.key === 'm' || e.key === 'M') && !e.metaKey && !e.ctrlKey) { Music.toggle(); actions('refreshMenu'); hud(); return; }
  if (e.key === 'Escape') {
    if (sc === 'play' && (UI.placing || UI.drag || UI.bend || UI.unhook || UI.carry || UI.selLine != null)) { UI.placing = null; UI.drag = null; UI.bend = null; UI.unhook = null; UI.carry = null; UI.selLine = null; return; }
    actions('escape');
    return;
  }
  if ((e.key === 'Delete' || e.key === 'Backspace') && playing() && selLine()) {
    send({ type: 'removeLine', line: UI.selLine }); UI.selLine = null; return;
  }
  if (e.code === 'Space' && sc === 'play' && NET() && NET().kind === 'local') { NET().paused = !NET().paused; e.preventDefault(); }
  // 1, 2, 3: game speed (solo only; online the server keeps everyone's clock)
  if (['Digit1', 'Digit2', 'Digit3'].includes(e.code) && sc === 'play' && NET() && NET().kind === 'local' && !e.metaKey && !e.ctrlKey) {
    NET().speed = +e.code.slice(5); NET().paused = false; toast(`Speed ${NET().speed}×`, 1200); hud();
  }
});

// ---------- quick hover descriptions; elementsFromPoint also finds disabled buttons ----------
let tipFor = null, tipTimer = 0;
addEventListener('pointermove', e => {
  const el = document.elementsFromPoint(e.clientX, e.clientY).find(n => n.dataset && n.dataset.tip);
  const key = el ? el.dataset.tip : null;
  if (key === tipFor) return;
  tipFor = key; clearTimeout(tipTimer);
  if (!el) { $('tip').classList.remove('show'); return; }
  tipTimer = setTimeout(() => {
    const [name, text] = key.split('|'), r = el.getBoundingClientRect(), tip = $('tip');
    $('tipName').textContent = name; $('tipText').textContent = text || '';
    // sit just outside the panel so the tip never covers its own buttons
    const edge = (el.closest('.card') || el).getBoundingClientRect().right, right = edge + 10 + 240 < innerWidth;
    tip.style.left = (right ? edge + 10 : Math.max(8, r.left)) + 'px';
    tip.style.top = (right ? r.top - 4 : r.bottom + 8) + 'px';
    tip.classList.add('show');
  }, 100);
});
